import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { firestore } from '../firebase.config'; // Ensure this path is correct
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { motion } from 'framer-motion';
import Sidebar from './Sidebar'; // Import Sidebar
import Loader from './Loader'; // Import Loader if needed
import JobApplicationsTable from './JobApplicationsTable';
import { Helmet } from 'react-helmet';

const EditProductForm = () => {
  const { productId } = useParams(); // Extract productId from URL
  const navigate = useNavigate(); // For navigation after actions
  const [applications, setApplications] = useState([]);
  // Initialize state with all required fields
  const [productData, setProductData] = useState({
    applicationDeadline: '',
    companyName: '',
    experienceRequired: '',
    id: '',
    jobDescription: '',
    jobTitle: '',
    jobType: '',
    location: '',
    numApplicants: '',
    postedDate: '',
    salary: '',
    salaryType: '',
  });

  // State for notifications
  const [field, setField] = useState(false);
  const [alertStatus, setAlertStatus] = useState('danger');
  const [msg, setMsg] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  
   const toggleSidebar = (state) => {
    setIsSidebarOpen(state);
  };
  // Fetch product data when the component mounts
  useEffect(() => {
    const fetchProductData = async () => {
      setIsLoading(true);
      try {
        const productDocRef = doc(firestore, 'Products', productId);
        const productSnapshot = await getDoc(productDocRef);
        if (productSnapshot.exists()) {
          setProductData({ id: productSnapshot.id, ...productSnapshot.data() });
          setIsLoading(false);
        } else {
          toast.error('Product not found.');
          navigate('/'); // Redirect to home or another appropriate page
        }
      } catch (error) {
        console.error('Error fetching product data:', error);
        toast.error('Failed to fetch product data. Please try again.');
        setIsLoading(false);
      }
    };

    fetchProductData();
  }, [productId, navigate]);

  // Fetch job applications when the productId changes
  useEffect(() => {
    const fetchJobApplications = async (jobId) => {
      try {
        const applicationsRef = collection(firestore, 'applications'); // Adjust the collection name if necessary
        const q = query(applicationsRef, where('jobId', '==', jobId));
        const querySnapshot = await getDocs(q);
        const apps = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setApplications(apps);
      } catch (error) {
        console.error('Error fetching job applications:', error);
        toast.error('Failed to fetch job applications. Please try again.');
      }
    };

    if (productId) {
      fetchJobApplications(productId);
    }
  }, [productId]);

  // Handle input changes for all form fields
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProductData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Handle changes in the ReactQuill editor
  const handleJobDescriptionChange = (value) => {
    setProductData((prevData) => ({
      ...prevData,
      jobDescription: value,
    }));
  };

  // Handle form submission to update the product data
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      // Validate required fields
      const {
        companyName,
        jobTitle,
        jobDescription,
        salary,
        salaryType,
        location,
        jobType,
        experienceRequired,
        applicationDeadline,
        numApplicants,
      } = productData;

      if (
        !companyName ||
        !jobTitle ||
        !jobDescription ||
        !salary ||
        !salaryType ||
        !location ||
        !jobType ||
        !experienceRequired ||
        !applicationDeadline ||
        !numApplicants
      ) {
        setField(true);
        setMsg("Required fields can't be empty");
        setAlertStatus('danger');
        setIsLoading(false);
        setTimeout(() => {
          setField(false);
        }, 4000);
        return;
      }

      const productDocRef = doc(firestore, 'Products', productId);
      await updateDoc(productDocRef, productData);
      setField(true);
      setMsg('Job updated successfully!');
      toast.success("Job updated successfully!");
      setAlertStatus('success');
      setIsLoading(false);
      setTimeout(() => {
        setField(false);
        navigate(location.pathname);
      }, 3000);
    } catch (error) {
      console.error('Error updating product:', error);
      setField(true);
      setMsg('Failed to update product. Please try again.');
      setAlertStatus('danger');
      toast.error("Failed to update product. Please try again!");
      setIsLoading(false);
      setTimeout(() => {
        setField(false);
      }, 4000);
    }
  };

  return (
    <div className='flex min-h-screen bg-gray-100'>
      <Helmet>
        <title>Edit Product - Nexify Infosystems</title>
        <meta name="description" content="View and manage job listings, job applications, and candidates on the Nexify admin dashboard." />
        <meta name="keywords" content="Nexify, dashboard, jobs, applications, candidates, admin, view jobs, view applications, view candidates" />
      </Helmet>
     
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />

      {/* Main Content */}
      <div className='flex-1 p-8 overflow-y-auto'>
        <div className='max-w-4xl mx-auto bg-white shadow-md rounded-lg p-6'>
          {/* Notifications */}
          {field && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className={`w-full p-4 mb-6 rounded-lg text-center text-lg font-semibold ${
                alertStatus === 'danger'
                  ? 'bg-red-100 text-red-700'
                  : 'bg-green-100 text-green-700'
              }`}
            >
              {msg}
            </motion.div>
          )}

          {/* Form Title */}
          <h2 className='text-2xl font-semibold mb-6 text-gray-800'>Job Details</h2>

          {/* Form */}
          <form onSubmit={handleFormSubmit} className='space-y-6'>
            {/* Grid Layout for Form Fields */}
            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              {/* Job Title */}
              <div className='flex flex-col'>
                <label className='mb-2 font-medium text-gray-700' htmlFor='jobTitle'>
                  Job Title
                </label>
                <input
                  id='jobTitle'
                  name='jobTitle'
                  type='text'
                  className='px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
                  required
                  value={productData.jobTitle}
                  placeholder='Enter Job Title'
                  onChange={handleInputChange}
                />
              </div>

              {/* Company Name */}
              <div className='flex flex-col'>
                <label className='mb-2 font-medium text-gray-700' htmlFor='companyName'>
                  Company Name
                </label>
                <input
                  id='companyName'
                  name='companyName'
                  type='text'
                  className='px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
                  required
                  value={productData.companyName}
                  placeholder='Enter Company Name'
                  onChange={handleInputChange}
                />
              </div>

              {/* Location */}
              <div className='flex flex-col'>
                <label className='mb-2 font-medium text-gray-700' htmlFor='location'>
                  Location
                </label>
                <input
                  id='location'
                  name='location'
                  type='text'
                  className='px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
                  required
                  value={productData.location}
                  placeholder='Enter Location'
                  onChange={handleInputChange}
                />
              </div>

              {/* Salary */}
              <div className='flex flex-col'>
                <label className='mb-2 font-medium text-gray-700' htmlFor='salary'>
                  Salary
                </label>
                <input
                  id='salary'
                  name='salary'
                  type='text'
                  className='px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
                  required
                  value={productData.salary}
                  placeholder='e.g., 50K-80K'
                  onChange={handleInputChange}
                />
              </div>

              {/* Salary Type */}
              <div className='flex flex-col'>
                <label className='mb-2 font-medium text-gray-700' htmlFor='salaryType'>
                  Salary Type
                </label>
                <select
                  id='salaryType'
                  name='salaryType'
                  className='px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
                  required
                  value={productData.salaryType}
                  onChange={handleInputChange}
                >
                  <option value=''>Select Salary Type</option>
                  <option value='monthly'>Monthly</option>
                  <option value='annual'>Annual</option>
                  <option value='weekly'>Weekly</option>
                  <option value='hourly'>Hourly</option>
                </select>
              </div>

              {/* Experience Required */}
              <div className='flex flex-col'>
                <label className='mb-2 font-medium text-gray-700' htmlFor='experienceRequired'>
                  Experience Required
                </label>
                <input
                  id='experienceRequired'
                  name='experienceRequired'
                  type='text'
                  className='px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
                  required
                  value={productData.experienceRequired}
                  placeholder='e.g., 2-3 Years'
                  onChange={handleInputChange}
                />
              </div>

              {/* Posted Date */}
              <div className='flex flex-col'>
                <label className='mb-2 font-medium text-gray-700' htmlFor='postedDate'>
                  Posted Date
                </label>
                <input
                  id='postedDate'
                  name='postedDate'
                  type='date'
                  className='px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
                  required
                  value={productData.postedDate}
                  onChange={handleInputChange}
                />
              </div>

              {/* Application Deadline */}
              <div className='flex flex-col'>
                <label className='mb-2 font-medium text-gray-700' htmlFor='applicationDeadline'>
                  Application Deadline
                </label>
                <input
                  id='applicationDeadline'
                  name='applicationDeadline'
                  type='date'
                  className='px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
                  required
                  value={productData.applicationDeadline}
                  onChange={handleInputChange}
                />
              </div>

              {/* Number of Applicants */}
              <div className='flex flex-col'>
                <label className='mb-2 font-medium text-gray-700' htmlFor='numApplicants'>
                  Number of Applicants
                </label>
                <input
                  id='numApplicants'
                  name='numApplicants'
                  type='number'
                  className='px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
                  required
                  min='0'
                  value={productData.numApplicants}
                  placeholder='Enter Number of Applicants'
                  onChange={handleInputChange}
                />
              </div>

              {/* Job Type */}
              <div className='flex flex-col'>
                <label className='mb-2 font-medium text-gray-700' htmlFor='jobType'>
                  Job Type
                </label>
                <select
                  id='jobType'
                  name='jobType'
                  className='px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
                  required
                  value={productData.jobType}
                  onChange={handleInputChange}
                >
                  <option value=''>Select Job Type</option>
                  <option value='full-time'>Full-Time</option>
                  <option value='part-time'>Part-Time</option>
                  <option value='contract'>Contract</option>
                  <option value='internship'>Internship</option>
                  <option value='temporary'>Temporary</option>
                </select>
              </div>

              {/* ID (Read-Only) */}
              <div className='flex flex-col'>
                <label className='mb-2 font-medium text-gray-700' htmlFor='id'>
                  Job ID
                </label>
                <input
                  id='id'
                  name='id'
                  type='text'
                  value={productData.id}
                  readOnly
                  className='px-4 py-2 bg-gray-100 border border-gray-200 rounded-md cursor-not-allowed'
                />
              </div>
            </div>

            {/* Job Description */}
            <div className='flex flex-col'>
              <label className='mb-2 font-medium text-gray-700' htmlFor='jobDescription'>
                Job Description
              </label>
              <ReactQuill
                id='jobDescription'
                value={productData.jobDescription}
                onChange={handleJobDescriptionChange}
                placeholder='Enter Job Description'
                className='h-48 md:h-64 lg:h-72 rounded-md border border-gray-300 mb-10'
                modules={{
                  toolbar: [
                    [{ header: '1' }, { header: '2' }, { font: [] }],
                    ['bold', 'italic', 'underline'],
                    ['link', 'image'],
                    [{ list: 'ordered' }, { list: 'bullet' }],
                    ['clean'],
                  ],
                }}
              />
            </div>

            {/* Action Buttons */}
            <div className='flex justify-end space-x-4'>
            
              <button
                type='submit'
                className='flex items-center px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition disabled:opacity-50'
                disabled={isLoading}
              >
                {isLoading ? <Loader /> : 'Update Job'}
              </button>
            </div>
          </form>
        </div>

        {/* Job Applications Section */}
        {applications.length > 0 && (
          <div className='mt-10'>
            <h2 className='text-2xl font-semibold mb-6 text-gray-800'>Job Applications</h2>
            <JobApplicationsTable applications={applications} />
          </div>
        )}

        {/* Toast Notifications */}
        <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} />
      </div>
    </div>
  );
};

export default EditProductForm;
